import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import {
  Brain, Trophy, ArrowsClockwise, ChartBar, Heart, Confetti, ArrowsLeftRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_CLS = {
  ativo: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  em_risco: "bg-amber-50 text-amber-700 border border-amber-200",
  perdido: "bg-rose-50 text-rose-700 border border-rose-200",
  pronto_para_retornar: "bg-indigo-50 text-indigo-700 border border-indigo-200",
};
const STATUS_LABEL = {
  ativo: "Ativo",
  em_risco: "Em risco",
  perdido: "Perdido",
  pronto_para_retornar: "Pronto pra retornar",
};
const STATUS_BAR_CLS = {
  ativo: "bg-emerald-500",
  em_risco: "bg-amber-500",
  pronto_para_retornar: "bg-indigo-500",
  perdido: "bg-rose-500",
};
const PERIOD_LABEL = {
  morning: "manhã",
  afternoon: "tarde",
  evening: "noite",
  night: "madrugada",
};

const fmtMoney = (cents) =>
  ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 0,
  });
const fmtNum = (n) => (n || 0).toLocaleString("pt-BR");

// ─── KPI card — identical pattern to ROI ────────────────────────────
function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 min-w-0" data-testid={`ci-kpi-${label}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium leading-snug">{label}</div>
        <div className="w-7 h-7 rounded-full bg-slate-50 text-slate-500 flex items-center justify-center shrink-0">
          <Icon size={13} weight="regular" />
        </div>
      </div>
      <div className="font-heading text-[28px] leading-none font-semibold text-slate-900 tracking-tight truncate">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-2 truncate">{sub}</div>}
    </div>
  );
}

// ─── Stacked distribution bar ──────────────────────────────────────
function StatusBar({ counts, total }) {
  const order = ["ativo", "em_risco", "pronto_para_retornar", "perdido"];
  return (
    <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
      {order.map((k) => {
        const v = counts[k] || 0;
        const w = total ? (v / total) * 100 : 0;
        if (!w) return null;
        return <div key={k} className={STATUS_BAR_CLS[k]} style={{ width: `${w}%` }} title={`${STATUS_LABEL[k]}: ${v}`} />;
      })}
    </div>
  );
}

export default function Intelligence() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState("");
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = async () => {
    const [s, sett] = await Promise.all([
      api.get("/intelligence/summary"),
      api.get("/intelligence/settings"),
    ]);
    setData(s.data);
    setSettings(sett.data);
  };

  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/intelligence/run");
      toast.success(`Inteligência atualizada — ${data.reactivation_events_created || 0} eventos de reativação gerados.`);
      load();
    } catch { toast.error("Falha ao recalcular"); }
    finally { setRunning(false); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/intelligence/settings", settings);
      toast.success("Parâmetros salvos");
      load();
    } catch { toast.error("Falha ao salvar"); }
    finally { setSavingSettings(false); }
  };

  if (!data || !settings) return <Topbar title="Perfil do cliente" subtitle="Carregando…" />;

  const m = data.metrics;
  const customers = filter ? data.customers.filter((c) => c.ci_score_status === filter) : data.customers;
  const returnRatePct = (m.return_rate * 100).toFixed(1);

  return (
    <>
      <Topbar
        title="Perfil do cliente"
        subtitle="Identifica quem voltou, quem está sumindo e quem é hora de chamar."
        actions={
          <button
            onClick={run}
            disabled={running}
            data-testid="ci-run-btn"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 transition-colors text-[12px] font-medium shadow-sm"
          >
            <Brain size={13} weight="regular" /> {running ? "Calculando…" : "Recalcular agora"}
          </button>
        }
      />

      <div className="p-6 md:p-8 space-y-8">
        {/* KPIs — 3 cols pattern from ROI */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ci-kpis">
          <KPI icon={Heart}           label="Total de clientes"    value={fmtNum(m.total_customers)} />
          <KPI icon={Trophy}          label="Presenças"            value={fmtNum(m.attended_count)} />
          <KPI icon={ArrowsClockwise} label="Reativados"           value={fmtNum(m.reactivated_count)} sub="Voltaram após período inativo" />
          <KPI icon={ChartBar}        label="Recorrentes"          value={fmtNum(m.returning_customers)} sub="Clientes que retornaram após 1ª visita" />
          <KPI icon={ArrowsLeftRight} label="Taxa de retorno"      value={`${returnRatePct}%`} sub="% dos atendidos que voltaram" />
          <KPI icon={Confetti}        label="Receita estimada"     value={fmtMoney(m.revenue_cents)} />
        </div>

        {/* Status distribution */}
        <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="ci-distribution">
          <header className="mb-4">
            <h3 className="font-heading font-semibold tracking-tight text-slate-900 text-[15px]">Como sua base está distribuída hoje</h3>
            <p className="text-xs text-slate-500 mt-0.5">Clique numa categoria pra filtrar a lista abaixo.</p>
          </header>
          <StatusBar counts={m.by_status || {}} total={m.total_customers} />
          <div className="flex flex-wrap gap-2 mt-4">
            {["ativo", "em_risco", "pronto_para_retornar", "perdido"].map((k) => {
              const v = (m.by_status || {})[k] || 0;
              const isActive = filter === k;
              return (
                <button
                  key={k}
                  onClick={() => setFilter(filter === k ? "" : k)}
                  data-testid={`ci-filter-${k}`}
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${STATUS_CLS[k]} ${isActive ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_BAR_CLS[k]}`} />
                  {STATUS_LABEL[k]}
                  <span className="font-mono tabular-nums opacity-70">{fmtNum(v)}</span>
                </button>
              );
            })}
            {filter && (
              <button
                onClick={() => setFilter("")}
                className="text-[11px] text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline ml-1"
              >
                Limpar filtro
              </button>
            )}
          </div>
        </section>

        {/* Parameters + Customer table */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Parameters card */}
          <section className="bg-white border border-slate-200 rounded-md p-5 space-y-4 lg:col-span-1" data-testid="ci-settings">
            <header>
              <h3 className="font-heading font-semibold tracking-tight text-slate-900 text-[15px]">Parâmetros</h3>
              <p className="text-xs text-slate-500 mt-0.5">Defina quando um cliente vira "em risco" ou "perdido".</p>
            </header>

            <SettingField label="Considera ativo até (dias)"  testid="ci-active-days"        value={settings.active_threshold_days}        onChange={(v) => setSettings({ ...settings, active_threshold_days: v })} />
            <SettingField label="Em risco até (dias)"          value={settings.at_risk_threshold_days}                                       onChange={(v) => setSettings({ ...settings, at_risk_threshold_days: v })} />
            <SettingField label="Perdido após (dias)"          value={settings.lost_threshold_days}                                          onChange={(v) => setSettings({ ...settings, lost_threshold_days: v })} />
            <SettingField label="Reativar após (dias)"         testid="ci-reactivation-days"  value={settings.inactivity_days_reactivation} onChange={(v) => setSettings({ ...settings, inactivity_days_reactivation: v })} />

            <label className="flex items-start gap-2 cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={settings.auto_reactivation_enabled}
                onChange={(e) => setSettings({ ...settings, auto_reactivation_enabled: e.target.checked })}
                data-testid="ci-auto-reactivation"
                className="mt-0.5"
              />
              <div>
                <div className="text-[13px] font-medium text-slate-800">Reativação automática</div>
                <div className="text-[11px] text-slate-500">Gera eventos automaticamente pra clientes inativos.</div>
              </div>
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Personalização das mensagens</span>
              <select
                value={settings.personalization_level}
                onChange={(e) => setSettings({ ...settings, personalization_level: e.target.value })}
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="off">Desligada</option>
                <option value="light">Leve</option>
                <option value="balanced">Balanceada</option>
                <option value="aggressive">Agressiva</option>
              </select>
            </label>

            <button
              onClick={saveSettings}
              disabled={savingSettings}
              data-testid="ci-save-settings"
              className="w-full inline-flex items-center justify-center px-4 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12px] font-medium shadow-sm disabled:opacity-60"
            >
              {savingSettings ? "Salvando…" : "Salvar parâmetros"}
            </button>
          </section>

          {/* Customer table */}
          <section className="lg:col-span-2 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="ci-customers-table">
            <header className="px-5 py-4 border-b border-slate-200">
              <h3 className="font-heading font-semibold tracking-tight text-slate-900 text-[15px]">
                Clientes <span className="text-slate-400 font-normal">· {fmtNum(customers.length)}</span>
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Engajamento, dias desde a última visita e sinais que a IA capturou.</p>
            </header>
            <div className="overflow-x-auto max-h-[640px] chat-scroll">
              <table className="w-full text-sm" style={{ minWidth: "780px" }}>
                <thead className="bg-slate-50 text-left sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">Cliente</th>
                    <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider text-right">Engajamento</th>
                    <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider text-right">Dias sem vir</th>
                    <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">Sinais</th>
                    <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">Melhor contato</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length === 0 && (
                    <tr><td colSpan="6" className="p-8 text-center text-slate-500 text-sm">
                      Nenhum cliente nessa categoria.
                    </td></tr>
                  )}
                  {customers.map((c) => {
                    const mem = c.ci_memory || {};
                    const tags = [];
                    if (mem.price_sensitive) tags.push("sensível ao preço");
                    if (mem.responds_fast === false) tags.push("responde lento");
                    if (mem.prefers_weekend) tags.push("prefere fds");
                    if (mem.total_no_show >= 2) tags.push("faltou antes");
                    const score = c.ci_engagement_score ?? 0;
                    return (
                      <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`ci-row-${c.id}`}>
                        <td className="px-4 py-3 min-w-0">
                          <div className="font-medium text-slate-900 truncate">{c.name}</div>
                          <div className="text-[11px] text-slate-400 font-mono mt-0.5">{c.phone}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-medium ${STATUS_CLS[c.ci_score_status] || "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                            <span className={`w-1 h-1 rounded-full ${STATUS_BAR_CLS[c.ci_score_status] || "bg-slate-400"}`} />
                            {STATUS_LABEL[c.ci_score_status] || c.ci_score_status || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="font-mono tabular-nums text-[13px] font-semibold text-slate-900">{c.ci_engagement_score ?? "—"}</div>
                          <div className="w-20 ml-auto h-1 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
                            <div className="h-full bg-slate-900 rounded-full transition-all" style={{ width: `${Math.min(100, score)}%` }} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-[13px] text-slate-700">
                          {c.ci_days_since_last ?? "—"}
                        </td>
                        <td className="px-4 py-3">
                          {tags.length === 0 ? (
                            <span className="text-[11px] text-slate-400">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {tags.map((t) => (
                                <span key={t} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full whitespace-nowrap">{t}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[12px] text-slate-600">
                          {c.ci_best_contact_time ? (PERIOD_LABEL[c.ci_best_contact_time] || c.ci_best_contact_time) : "—"}
                          {c.ci_best_contact_day && <span className="text-slate-400"> · {c.ci_best_contact_day}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function SettingField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
      />
    </label>
  );
}
